import { useParams } from "react-router-dom";
import { useModels } from "@/queries/models";
import { useAuth } from "@/context/auth-context";
import LoadingSpinner from "@/components/LoadingSpinner";

import { getParameters } from "./utils";
import { PromptParametersProps } from "./types";
import { ParametersWithTitle } from "./ParametersWithTitle";
import { ModelAndProviderPreview } from "./ModelAndProviderPreview";

export const ParametersPreviewContent = ({
  promptData,
  isChat,
  titleClassName,
  showLearnMore = true,
}: Omit<PromptParametersProps, "displayAsPopover">) => {
  const { workspaceId } = useParams();
  const auth = useAuth();

  const { data: modelConfigs, isLoading: isLoadingModels } = useModels(
    auth?.userToken!,
    workspaceId!,
  );

  if (isLoadingModels) {
    return (
      <div className="flex h-full w-[132px] items-center justify-center">
        <LoadingSpinner size={4} />
      </div>
    );
  }

  const parameters = getParameters(promptData, modelConfigs || [], isChat);

  const modelName =
    promptData?.metadata?.model?.name || promptData?.inference_client_name;

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className={`text-base font-medium leading-none ${titleClassName}`}>
          Parameters
        </h4>
        {showLearnMore && (
          <a
            className="text-xs text-blue-500 hover:text-blue-400"
            target="_blank"
            rel="noreferrer"
            href="https://docs.promptlayer.com/features/prompt-registry#metadata-2"
          >
            Learn more
          </a>
        )}
      </div>
      {modelName ? (
        <div
          className="max-h-[53vh] overflow-y-auto"
          style={{ scrollbarGutter: "stable" }}
        >
          <div className="w-full pb-2">
            <ModelAndProviderPreview promptData={promptData} />
            <ParametersWithTitle parameters={parameters} />
          </div>
        </div>
      ) : (
        <p className="text-sm italic text-gray-400">
          No parameters set for this prompt version
        </p>
      )}
    </div>
  );
};
